import React, { useState, useContext, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ActivityIndicator,
  Alert,
  KeyboardAvoidingView,
  Platform,
  ScrollView
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { AuthContext } from '../contexts/AuthContext';
import { config } from '../config';
import hapticService from '../services/hapticService';
import alertService from '../services/alertService';

const LoginScreen = ({ navigation }) => {
  const { user, isLoading, error, signIn } = useContext(AuthContext);
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isConnecting, setIsConnecting] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const [qrDetected, setQrDetected] = useState(false);
  
  // Refs pour éviter les re-renders
  const retryCount = useRef(0);
  const maxRetries = 5;
  const hasProcessedAuth = useRef(false);

  // Charger les paramètres haptiques au démarrage
  useEffect(() => {
    hapticService.loadSettings();
  }, []);

  // 🔧 FONCTION EXTRACTION AUTH AMÉLIORÉE
  const extractAuthFromURL = () => {
    if (typeof window === 'undefined') return null;
    
    try {
      // Method 1: URL search params
      const urlParams = new URLSearchParams(window.location.search);
      let authParam = urlParams.get('auth');
      
      // Method 2: Hash search params
      if (!authParam && window.location.hash.includes('?auth=')) {
        const hashParts = window.location.hash.split('?');
        if (hashParts.length > 1) {
          const hashParams = new URLSearchParams(hashParts[1]);
          authParam = hashParams.get('auth');
        }
      }
      
      // Method 3: Manual parsing for malformed URLs
      if (!authParam) {
        const fullUrl = window.location.href;
        const authMatch = fullUrl.match(/[?&]auth=([^&]+)/);
        if (authMatch) {
          authParam = authMatch[1];
        }
      }
      
      return authParam;
    } catch (error) {
      console.error('Error extracting auth param:', error);
      return null;
    }
  };

  // 🔧 FONCTION DE TRAITEMENT QR AMÉLIORÉE
  const processQRAuth = async (authParam, isRetry = false) => {
    if (hasProcessedAuth.current && !isRetry) {
      console.log('🔍 QR auth already processed, skipping');
      return;
    }

    try {
      console.log('🔍 Processing QR auth parameter:', authParam?.substring(0, 20) + '...');
      
      // Décoder les credentials
      const decoded = atob(authParam);
      const credentials = JSON.parse(decoded);
      
      if (!credentials.email || !credentials.password) {
        throw new Error('Missing credentials in QR code');
      }

      console.log('✅ QR credentials decoded for:', credentials.email);
      hasProcessedAuth.current = true;
      
      // 🎯 OPTION 1: PRE-REMPLIR DIRECTEMENT (Recommandé pour Android)
      setEmail(credentials.email);
      setPassword(credentials.password);
      setQrDetected(true);
      
      // Auto-login immédiat ou demander confirmation
      if (Platform.OS === 'android' || Platform.OS === 'ios') {
        // Sur mobile: Auto-login direct pour meilleure UX
        console.log('📱 Mobile detected - attempting direct auto-login');
        await performAutoLogin(credentials.email, credentials.password);
      } else {
        // Sur web: Demander confirmation
        alertService.showAlert(
          '🔐 QR Code Detected',
          `Login credentials found for:\n${credentials.email}\n\nProceed with automatic login?`,
          [
            { 
              text: 'Cancel', 
              style: 'cancel',
              onPress: () => {
                setQrDetected(false);
                cleanupURL();
              }
            },
            { 
              text: 'Login', 
              onPress: async () => {
                await performAutoLogin(credentials.email, credentials.password);
              }
            }
          ]
        );
      }
      
    } catch (decodeError) {
      console.error('❌ Failed to decode QR auth:', decodeError);
      hapticService.error();
      alertService.showAlert(
        'Invalid QR Code', 
        'The QR code is invalid or corrupted. Please try manual login.'
      );
      setQrDetected(false);
      hasProcessedAuth.current = true; // Marquer comme traité pour éviter les boucles
    }
  };

  // 🔧 CLEANUP URL
  const cleanupURL = () => {
    if (typeof window !== 'undefined') {
      const newUrl = window.location.origin + window.location.pathname + '#/login';
      window.history.replaceState(null, '', newUrl);
    }
  };

  // 🔧 RETRY MECHANISM AMÉLIORÉ
  const retryQRDetection = () => {
    if (retryCount.current >= maxRetries || hasProcessedAuth.current) {
      console.log('🔍 QR retry limit reached or already processed');
      setAutoLoginAttempted(true);
      return;
    }

    const authParam = extractAuthFromURL();
    if (authParam) {
      console.log(`🔄 QR retry attempt ${retryCount.current + 1}/${maxRetries}`);
      retryCount.current++;
      processQRAuth(authParam, true);
    } else {
      retryCount.current++;
      // Continuer à retry pendant un certain temps
      if (retryCount.current < maxRetries) {
        setTimeout(retryQRDetection, 1000);
      } else {
        setAutoLoginAttempted(true);
      }
    }
  };

  // 🔐 AUTO-LOGIN PRINCIPAL avec POLLING AGRESSIF
  useEffect(() => {
    if (hasProcessedAuth.current) return;
    
    let pollCount = 0;
    const maxPolls = 20; // 10 secondes total
    
    const pollForAuth = () => {
      if (hasProcessedAuth.current || pollCount >= maxPolls) {
        setAutoLoginAttempted(true);
        return;
      }
      
      const authParam = extractAuthFromURL();
      console.log(`🔍 Poll ${pollCount + 1}/${maxPolls} - Auth found:`, !!authParam);
      
      if (authParam) {
        processQRAuth(authParam);
        return;
      }
      
      pollCount++;
      setTimeout(pollForAuth, 500);
    };

    // Démarrer immédiatement puis toutes les 500ms
    pollForAuth();
  }, []);

  // 🔧 SURVEILLANCE URL DIRECTE (bypass React Navigation)
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    let checkInterval;
    
    const startUrlWatching = () => {
      checkInterval = setInterval(() => {
        if (hasProcessedAuth.current) {
          clearInterval(checkInterval);
          return;
        }
        
        const authParam = extractAuthFromURL();
        if (authParam && !qrDetected) {
          console.log('🔍 URL watcher found auth param');
          processQRAuth(authParam);
          clearInterval(checkInterval);
        }
      }, 200);
    };

    // Démarrer la surveillance après un court délai
    const timer = setTimeout(startUrlWatching, 100);
    
    return () => {
      clearTimeout(timer);
      if (checkInterval) clearInterval(checkInterval);
    };
  }, [qrDetected]);

  // 🔧 FORCE REFRESH EN DERNIER RECOURS (Android/iOS uniquement)
  useEffect(() => {
    // Vérifier après 3 secondes si on a raté un QR code sur mobile
    const forceRefreshTimer = setTimeout(() => {
      if (Platform.OS === 'android' || Platform.OS === 'ios') {
        if (!hasProcessedAuth.current && !autoLoginAttempted) {
          const authParam = extractAuthFromURL();
          if (authParam && !email && !password) {
            console.log('🔄 Force refresh detection - reloading page for QR processing');
            if (typeof window !== 'undefined') {
              window.location.reload();
            }
          }
        }
      }
    }, 3000);

    return () => clearTimeout(forceRefreshTimer);
  }, [email, password, autoLoginAttempted]);

  // Fonction pour effectuer l'auto-login
  const performAutoLogin = async (emailValue, passwordValue) => {
    hapticService.button();
    setIsConnecting(true);
    
    try {
      console.log('🔑 Attempting QR auto-login for:', emailValue);
      
      const success = await signIn(emailValue, passwordValue, config.GOOGLE_SHEET_ID);
      
      if (success) {
        console.log('✅ QR auto-login successful');
        hapticService.success();
        
        // Message de succès seulement sur web
        if (Platform.OS === 'web') {
          alertService.showAlert(
            'Login Successful',
            'You have been automatically logged in via QR code!'
          );
        }
        
        cleanupURL();
      } else {
        console.log('❌ QR auto-login failed');
        hapticService.error();
        alertService.showAlert(
          'QR Login Failed', 
          error || 'The credentials from the QR code are invalid. Please try manual login.'
        );
      }
    } catch (loginError) {
      console.error('❌ QR auto-login error:', loginError);
      hapticService.error();
      alertService.showAlert(
        'Login Error',
        'An error occurred during QR login. Please try manual login.'
      );
    } finally {
      setIsConnecting(false);
      setQrDetected(false);
    }
  };

  useEffect(() => {
    if (user) {
      hapticService.success();
      navigation.replace('Home');
    }
  }, [user, navigation]);

  const handleLogin = async () => {
    if (!email || !password) {
      hapticService.error();
      alertService.showAlert('Error', 'Please fill all fields');
      return;
    }

    hapticService.button();
    
    setIsConnecting(true);
    const success = await signIn(email, password, config.GOOGLE_SHEET_ID);
    setIsConnecting(false);

    if (!success && error) {
      hapticService.error();
      alertService.showAlert('Login Failed', error);
    }
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    const forceCheck = () => {
      const currentUrl = window.location.href;
      console.log('🔍 Force check URL:', currentUrl);
      
      if (currentUrl.includes('auth=') && !hasProcessedAuth.current && !qrDetected) {
        console.log('🚨 FORCE REFRESH - Auth detected but not processed');
        // Option 1: Force reload
        window.location.reload();
        
        // Option 2: Force extraction directe (alternative)
        // const match = currentUrl.match(/auth=([^&]+)/);
        // if (match) processQRAuth(match[1]);
      }
    };

    const timer = setTimeout(forceCheck, 2000);
    return () => clearTimeout(timer);
  }, [qrDetected]);


  // Fonction pour générer un QR code pour l'utilisateur actuel (pour debug/admin)
  const generateQRCode = () => {
    if (!email || !password) {
      alertService.showAlert('Error', 'Please enter credentials first');
      return;
    }
    
    try {
      const credentials = { email, password };
      const encoded = btoa(JSON.stringify(credentials));
      const qrUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://tls.gfphoto.ch'}/#/login?auth=${encoded}`;
      
      console.log('🎯 Generated QR URL:', qrUrl);
      
      if (typeof window !== 'undefined' && navigator.clipboard) {
        navigator.clipboard.writeText(qrUrl);
        alertService.showAlert('QR URL Generated', 'URL copied to clipboard!\n\nGenerate a QR code from this URL to enable automatic login.');
      } else {
        alertService.showAlert('QR URL Generated', qrUrl);
      }
    } catch (error) {
      console.error('❌ QR generation error:', error);
      alertService.showAlert('Error', 'Failed to generate QR code');
    }
  };

  return (
    <SafeAreaView style={styles.container}>
      <KeyboardAvoidingView
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
        style={styles.keyboardAvoidingView}
      >
        <ScrollView contentContainerStyle={styles.scrollView}>
          <View style={styles.header}>
            <Text style={styles.title}>Trials Live Scoring</Text>
            <Text style={styles.subtitle}>UCI Trials Races Scoring App</Text>
          </View>

          {/* Indicateur de QR Code détecté - Seulement sur web maintenant */}
          {qrDetected && Platform.OS === 'web' && (
            <View style={styles.qrDetectedContainer}>
              <Text style={styles.qrDetectedTitle}>🔐 QR Code Detected</Text>
              <Text style={styles.qrDetectedText}>
                Automatic login credentials found. Check the popup for confirmation.
              </Text>
            </View>
          )}

          {/* Message d'auto-login pour mobile */}
          {qrDetected && (Platform.OS === 'android' || Platform.OS === 'ios') && (
            <View style={styles.qrDetectedContainer}>
              <Text style={styles.qrDetectedTitle}>🔐 QR Login Active</Text>
              <Text style={styles.qrDetectedText}>
                Credentials loaded from QR code. Attempting automatic login...
              </Text>
            </View>
          )}
          
          <View style={styles.form}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={[styles.input, qrDetected && styles.inputHighlighted]}
              placeholder="your.email@example.com"
              value={email}
              onChangeText={setEmail}
              autoCapitalize="none"
              autoCorrect={false}
              keyboardType="email-address"
              onFocus={() => hapticService.selection()}
            />

            <Text style={styles.label}>Password</Text>
            <TextInput
              style={[styles.input, qrDetected && styles.inputHighlighted]}
              placeholder="Password from Authorizations sheet"
              value={password}
              onChangeText={setPassword}
              secureTextEntry
              autoCapitalize="none"
              autoCorrect={false}
              onFocus={() => hapticService.selection()}
            />
            
            {error && (
              <Text style={styles.errorText}>{error}</Text>
            )}
            
            <TouchableOpacity
              style={styles.loginButton}
              onPress={handleLogin}
              disabled={isConnecting || isLoading}
            >
              {isConnecting || isLoading ? (
                <ActivityIndicator color="#fff" />
              ) : (
                <Text style={styles.loginButtonText}>Sign In</Text>
              )}
            </TouchableOpacity>

            {/* Bouton de génération QR pour debug/admin - seulement en développement */}
            {__DEV__ && (
              <TouchableOpacity
                style={styles.qrButton}
                onPress={generateQRCode}
              >
                <Text style={styles.qrButtonText}>🎯 Generate QR URL (Debug)</Text>
              </TouchableOpacity>
            )}

            <View style={styles.info}>
              <Text style={styles.infoText}>
                Use the email and password from the "Authorizations" sheet
              </Text>
              <Text style={styles.infoText}>
                📱 Or scan a QR code for automatic login
              </Text>
            </View>
          </View>
          
          <View style={styles.footer}>
            <Text style={styles.copyright}>© Gabriele Fusco / VTM 2025</Text>
            <Text style={styles.version}>v{config.APP_VERSION}</Text>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  keyboardAvoidingView: {
    flex: 1,
  },
  scrollView: {
    flexGrow: 1,
    padding: 20,
    justifyContent: 'space-between',
  },
  header: {
    alignItems: 'center',
    marginTop: 60,
    marginBottom: 40,
  },
  title: {
    fontSize: 28,
    
    color: '#0066cc',
    marginBottom: 8,
    fontFamily: 'Montserrat_700Bold',
  },
  subtitle: {
    fontSize: 16,
    color: '#555',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  qrDetectedContainer: {
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    padding: 16,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: '#0066cc',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 3,
  },
  qrDetectedTitle: {
    fontSize: 16,
    
    color: '#0066cc',
    marginBottom: 8,
    fontFamily: 'Montserrat_600SemiBold',
  },
  qrDetectedText: {
    fontSize: 14,
    color: '#0066cc',
    fontFamily: 'Montserrat_400Regular',
    lineHeight: 20,
  },
  form: {
    width: '100%',
    maxWidth: 400,
    alignSelf: 'center',
  },
  label: {
    fontSize: 16,
    
    marginBottom: 8,
    color: '#333',
    fontFamily: 'Montserrat_600SemiBold',
  },
  input: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: '#ddd',
    borderRadius: 8,
    padding: 12,
    fontSize: 16,
    marginBottom: 16,
    fontFamily: 'Montserrat_400Regular',
  },
  inputHighlighted: {
    borderColor: '#0066cc',
    borderWidth: 2,
    backgroundColor: '#f8fcff',
  },
  errorText: {
    color: 'red',
    marginBottom: 16,
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
  },
  loginButton: {
    backgroundColor: '#0066cc',
    borderRadius: 8,
    padding: 14,
    alignItems: 'center',
    marginBottom: 16,
  },
  loginButtonText: {
    color: '#fff',
    fontSize: 16,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  qrButton: {
    backgroundColor: '#4CAF50',
    borderRadius: 8,
    padding: 12,
    alignItems: 'center',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#45a049',
  },
  qrButtonText: {
    color: '#fff',
    fontSize: 14,
    
    fontFamily: 'Montserrat_600SemiBold',
  },
  info: {
    backgroundColor: '#e3f2fd',
    borderRadius: 8,
    padding: 12,
    marginTop: 8,
  },
  infoText: {
    fontSize: 14,
    color: '#0066cc',
    textAlign: 'center',
    fontFamily: 'Montserrat_400Regular',
    marginBottom: 4,
  },
  footer: {
    marginTop: 40,
    alignItems: 'center',
  },
  copyright: {
    fontSize: 14,
    color: '#666',
    marginBottom: 4,
    fontFamily: 'Montserrat_400Regular',
  },
  version: {
    fontSize: 12,
    color: '#888',
    fontFamily: 'Montserrat_400Regular',
  },
});

export default LoginScreen;